import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { IconCalendar } from '../../assets/icons/IconCalendar';
import { IconClock } from '../../assets/icons/IconClock';
import { IconFileText } from '../../assets/icons/IconFileText';
import { TranslationKey } from '../../i18n/translationKeys';
import { useLanguageStore } from '../../stores/languageStore';
import { Tag } from '../../ui/Tag/Tag';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import { RelanceAgendaDetail } from './RelanceAgendaDetail';
import { RelanceAgendaTag } from './RelanceAgendaTag';
import { RelanceRowMenu } from './RelanceRowMenu';

interface RelanceAgendaRowProps {
    entry: RelanceAgendaEntry;
    showDate: boolean;
    canCollectPayment: boolean;
    onOpen: () => void;
}

// Snoozed and ignored rows share the deactivated-row test ids.
const testIdPrefix = (status: RelanceAgendaEntry['status']) => {
    if (status === 'snoozed' || status === 'ignored') return 'deactivated-row';
    return status === 'expected' ? 'expected-row' : 'home-task-item';
};

// French writes the weekday in lower case inside a sentence ("reportée au lun 26 oct").
const upcomingDateLabel = (entry: RelanceAgendaEntry, t: TFunction, language: string): string => {
    const date = language.startsWith('fr') ? entry.dateLabel.charAt(0).toLowerCase() + entry.dateLabel.slice(1) : entry.dateLabel;
    if (entry.status === 'snoozed') return t(TranslationKey.Home.Agenda.SnoozedUntil, { date });
    if (entry.status === 'expected') return t(TranslationKey.Home.Agenda.ExpectedOn, { date });
    return entry.dateLabel;
};

const CELLS_CLASS = 'col-start-1 -col-end-2 row-start-1 row-span-2 @relance-row:row-span-1 grid grid-cols-subgrid grid-rows-subgrid items-center gap-y-m h-full text-left text-s font-normal';

export const RelanceAgendaRow: React.FC<RelanceAgendaRowProps> = ({ entry, showDate, canCollectPayment, onOpen }) => {
    const { t } = useTranslation();
    const lang = useLanguageStore(s => s.lang);
    const { item, status } = entry;

    const prefix = testIdPrefix(status);
    const testId = `${prefix}-${item.id}`;
    const sent = status === 'sent';
    // Sent and expected rows are inert: no hover, no click.
    const inert = sent || status === 'expected';
    const { overdueDocumentCount: documents } = item;
    const invoiceCount = documents !== null && documents > 0 ? t(TranslationKey.Home.Agenda.InvoiceCount, { count: documents }) : null;
    const daysLate = item.daysLate !== null ? t(TranslationKey.Home.Agenda.DaysLate, { count: item.daysLate }) : null;

    const cells = (
        <>
            <span className={`col-start-1 row-start-1 @relance-row:col-auto @relance-row:row-auto min-w-0 truncate font-semibold${sent ? '' : ' text-content'}`}>{item.title}</span>
            <span className="col-start-1 col-end-3 row-start-2 flex min-w-0 flex-wrap items-center gap-x-l gap-y-xs @relance-row:contents">
                <span className="flex gap-xs">
                    <RelanceAgendaTag entry={entry} testId={`${prefix}-tag-${item.id}`} />
                    {item.creditLimitPercent != null && (
                        <Tag tone="warning" testId={`${prefix}-credit-limit-${item.id}`}>
                            {t(TranslationKey.RelanceDescription.CreditLimit, { count: item.creditLimitPercent })}
                        </Tag>
                    )}
                </span>
                {showDate ? (
                    <RelanceAgendaDetail icon={<IconCalendar />} label={upcomingDateLabel(entry, t, lang)} hint={t(status === 'expected' ? TranslationKey.Home.Agenda.ExpectedHint : TranslationKey.Home.Agenda.DueDateHint)} muted={sent} testId={`${prefix}-date-${item.id}`} />
                ) : (
                    <RelanceAgendaDetail icon={<IconFileText />} label={invoiceCount} hint={t(TranslationKey.Home.Agenda.InvoiceCountHint)} muted={sent} testId={`${prefix}-invoices-${item.id}`} />
                )}
                <RelanceAgendaDetail icon={<IconClock />} label={daysLate} hint={t(TranslationKey.Home.Agenda.DaysLateHint)} muted={sent} testId={`${prefix}-days-late-${item.id}`} />
            </span>
            <span className={`col-start-2 row-start-1 @relance-row:col-auto @relance-row:row-auto translate-x-s @relance-row:translate-x-s font-semibold tabular-nums whitespace-nowrap text-right${sent ? '' : ' text-content'}`}>{item.amount}</span>
        </>
    );

    // Below the relance-row width the cells are placed on two lines, the tag, invoice count or due
    // date, and delay flowing together on the second; from it up the details wrapper dissolves
    // (display: contents) and every cell auto-fills the list's one-line columns in source order.
    return (
        <li data-testid={testId} data-status={status} className={`col-span-full grid grid-cols-subgrid content-center items-center gap-x-m gap-y-m min-h-21.5 @relance-row:min-h-0 @relance-row:h-13.5 px-l rounded-lg border border-line-card bg-surface-tertiary shadow-card ${inert ? '' : 'transition-colors hover:bg-primary-200'}`}>
            {status === 'expected' ? (
                <div data-testid={`${prefix}-content-${item.id}`} className={`${CELLS_CLASS} text-content-secondary`}>{cells}</div>
            ) : (
                <button
                    type="button"
                    data-testid={`${prefix}-btn-${item.id}`}
                    onClick={onOpen}
                    disabled={sent}
                    className={`${CELLS_CLASS} ${sent ? 'text-content-tertiary' : 'text-content-secondary cursor-pointer'} focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-focus-ring`}
                >
                    {cells}
                </button>
            )}
            {/* The shift makes this a stacking context, so it is raised while open or the rows below cover the menu. */}
            <span className="relative flex translate-x-xs has-aria-expanded:z-20">
                <RelanceRowMenu entry={entry} canCollectPayment={canCollectPayment} testIdPrefix={prefix} />
            </span>
        </li>
    );
};
